// src/components/admin/Dashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Users,
  FileText,
  CheckCircle,
  Upload,
  Archive,
  UserPlus,
  Search,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Mail,
  AlertTriangle
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { showToast } from '../../utils/toast';
import axios from '../../utils/axios';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import SubmissionDetailsModal from './modals/SubmissionDetailsModal';
import { formatStatus, getStatusClass } from '../../utils/utils.global';

// ── Status / type option lists ───────────────────────────────────────────────
const STATUS_OPTIONS = [
  'all',
  'submitted',
  'under_review',
  'review_completed',
  'revision_required',
  'accepted',
  'rejected',
  'archived',
];

const TYPE_OPTIONS = [
  'all',
  'Research Article',
  'Review Article',
  'Case Study',
  'Short Communication',
  'Editorial',
  'Letter to the Editor',
  'Perspective',
  'Book Review',
];

// ── FilterChip ───────────────────────────────────────────────────────────────
function FilterChip({ label, onClear }) {
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
      {label}
      <button
        onClick={onClear}
        className="hover:bg-purple-200 rounded-full p-0.5"
        aria-label={`Clear ${label}`}
      >
        ×
      </button>
    </span>
  );
}

// ── Custom Tooltip for chart ─────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 shadow-lg rounded-lg border border-gray-200">
        <p className="text-sm font-medium text-gray-900">{label}</p>
        <p className="text-sm text-[--asfi-purple]">
          Submissions: <span className="font-bold">{payload[0].value}</span>
        </p>
      </div>
    );
  }
  return null;
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [showActionsDropdown, setShowActionsDropdown] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiveSubmissionId, setArchiveSubmissionId] = useState(null);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  // ── Filter state ───────────────────────────────────────────────────────────
  const [filters, setFilters] = useState({
    dateFrom: "",
    dateTo: "",
    status: "all",
    type: "all",
    isWomenInScience: false,
    isKidnappingForRansom: false,
    isBelispointAcademic: false,
  });
  const [showFilters, setShowFilters] = useState(false);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({
      dateFrom: "",
      dateTo: "",
      status: "all",
      type: "all",
      isWomenInScience: false,
      isKidnappingForRansom: false,
      isBelispointAcademic: false,
    });
    setCurrentPage(1);
  };

  const activeFilterCount =
    (filters.dateFrom ? 1 : 0) +
    (filters.dateTo ? 1 : 0) +
    (filters.status !== "all" ? 1 : 0) +
    (filters.type !== "all" ? 1 : 0) +
    (filters.isWomenInScience ? 1 : 0) +
    (filters.isKidnappingForRansom ? 1 : 0) +
    (filters.isBelispointAcademic ? 1 : 0);

  // ── Combine files state ────────────────────────────────────────────────────
  const [combiningFiles, setCombiningFiles] = useState(false);
  const [combineProgress, setCombineProgress] = useState(0);
  const [combineResult, setCombineResult] = useState(null);

  const handleCombineFiles = async (submissionId) => {
    try {
      setCombiningFiles(true);
      setCombineProgress(10);
      setCombineResult(null);

      showToast('Combining files... This may take a moment.', 'info');

      const response = await axios.post('/external/api/combinePDF', {
        revisionId: submissionId
      }, {
        timeout: 120000,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setCombineProgress(30 + Math.floor(percentCompleted * 0.3));
        }
      });

      setCombineProgress(90);

      if (response.data.success) {
        setCombineResult(response.data);

        if (response.data.combinedFile) {
          const link = document.createElement('a');
          link.href = response.data.combinedFile;
          link.setAttribute('download', `combined_${submissionId}.pdf`);
          link.setAttribute('target', '_blank');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }

        showToast(`Files combined successfully! ${response.data.fileCount} files processed.`, 'success');

        console.log('Original files:', response.data.originalFiles);
        console.log('PDF files:', response.data.pdfFiles);
        console.log('Combined file:', response.data.combinedFile);
      } else {
        showToast(response.data.message || 'Failed to combine files', 'error');
      }
    } catch (error) {
      console.error('Error combining files:', error);
      showToast(error.response?.data?.message || 'Failed to combine files. Please try again.', 'error');
    } finally {
      setCombineProgress(100);
      setCombiningFiles(false);
      setTimeout(() => setCombineProgress(0), 3000);
    }
  };

  const [stats, setStats] = useState({
    submissions: 0,
    authors: 0,
    reviewed: 0,
    decisioned: 0,
    archived: 0,
    editorInvitations: 0
  });
  const [recentSubmissions, setRecentSubmissions] = useState([]);

  // Chart data
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartType, setChartType] = useState('line');
  const [timeRange, setTimeRange] = useState('30');

  // Reminder modal state
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderData, setReminderData] = useState({
    reviewId: null,
    articleId: null,
    reviewerEmail: null,
    dueDate: null,
    daysOverdue: 0
  });
  const [customReminderMessage, setCustomReminderMessage] = useState('');
  const [reminderLoading, setReminderLoading] = useState(false);

  const handleSendReminder = async () => {
    console.log(reminderData);

    if (!reminderData.reviewId || !reminderData.articleId || !reminderData.reviewerEmail) {
      showToast('Missing reminder data', 'error');
      return;
    }

    setReminderLoading(true);

    try {
      const response = await axios.post('/editors/send-review-reminder', {
        reviewId: reminderData.reviewId,
        articleId: reminderData.articleId,
        reviewerEmail: reminderData.reviewerEmail,
        reminderType: 'manual',
        customMessage: customReminderMessage,
        dueDate: reminderData.dueDate,
        daysOverdue: reminderData.daysOverdue
      });

      if (response.data.status === 'success') {
        showToast(response.data.message, 'success');

        setShowReminderModal(false);
        setCustomReminderMessage('');
        setReminderData({
          reviewId: null,
          articleId: null,
          reviewerEmail: null,
          dueDate: null,
          daysOverdue: 0
        });
      } else {
        showToast(response.data.message || 'Failed to send reminder', 'error');
      }
    } catch (error) {
      console.error('Error sending reminder:', error);
      showToast(error.response?.data?.message || 'Failed to send reminder', 'error');
    } finally {
      setReminderLoading(false);
    }
  };

  const handleOpenReminderModal = (invitation) => {
    let daysOverdue = 0;
    if (invitation.dueDate) {
      const dueDate = new Date(invitation.dueDate);
      const today = new Date();
      if (dueDate < today) {
        daysOverdue = Math.ceil((today - dueDate) / (1000 * 60 * 60 * 24));
      }
    }

    setReminderData({
      reviewId: invitation.id,
      articleId: selectedSubmission.revision_id || invitation.invitation_link,
      reviewerEmail: invitation.reviewer_email || invitation.invited_user,
      dueDate: invitation.dueDate || invitation.invitation_expiry_date || invitation.invitationDate,
      daysOverdue
    });
    setShowReminderModal(true);
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const getSubmissionsEndpoint = useCallback(() => {
    if (user?.editorialLevel === 'editor_in_chief' || user?.editorialLevel === 'editor-in-chief' || user?.editorialLevel === 'editorial_assistant') {
      return '/editors/all-submissions';
    }
    return '/editors/my-submissions';
  }, [user?.editorialLevel]);

  const fetchChartData = useCallback(async () => {
    setChartLoading(true);
    try {
      const days = parseInt(timeRange);
      const data = [];
      const today = new Date();

      for (let i = days; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);

        const dayOfWeek = date.getDay();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
        let baseCount = isWeekend ? 2 : 5;

        const randomFactor = Math.floor(Math.random() * 5) + 1;
        const isPeakDay = i % 7 === 3;

        let count = baseCount + randomFactor;
        if (isPeakDay) count += 8;

        data.push({
          date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          fullDate: date.toISOString().split('T')[0],
          submissions: count,
          dayOfWeek: date.toLocaleDateString('en-US', { weekday: 'short' })
        });
      }

      setChartData(data);
    } catch (error) {
      console.error('Error fetching chart data:', error);
    } finally {
      setChartLoading(false);
    }
  }, [timeRange]);

  // Fetch stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get('/editors/backend/editors/dashboard-stats');
        if (response.data.success) {
          setStats(response.data.stats);
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
        showToast('Failed to load dashboard statistics', 'error');
      }
    };

    fetchStats();
    fetchChartData();
  }, [fetchChartData]);

  // Fetch submissions with debounced search + filters
  useEffect(() => {
    const fetchSubmissions = async () => {
      setLoading(true);
      try {
        const endpoint = getSubmissionsEndpoint();

        const params = {
          page: currentPage,
          limit: itemsPerPage,
          search: debouncedSearchTerm,
        };

        if (filters.dateFrom)              params.dateFrom = filters.dateFrom;
        if (filters.dateTo)                params.dateTo = filters.dateTo;
        if (filters.status !== "all")      params.status = filters.status;
        if (filters.type   !== "all")      params.type = filters.type;
        if (filters.isWomenInScience)      params.isWomenInScience = "1";
        if (filters.isKidnappingForRansom) params.isKidnappingForRansom = "1";
        if (filters.isBelispointAcademic)  params.isBelispointAcademic = "1";

        const response = await axios.get(endpoint, { params });

        if (response.data.success) {
          setRecentSubmissions(response.data.submissions);
          setTotalItems(response.data.total);
        } else if (response.data.submissions) {
          setRecentSubmissions(response.data.submissions);
          setTotalItems(response.data.total || response.data.submissions.length);
        }
      } catch (error) {
        console.error('Error fetching submissions:', error);
        showToast('Failed to load submissions', 'error');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchSubmissions();
    }
  }, [currentPage, itemsPerPage, debouncedSearchTerm, user, getSubmissionsEndpoint, filters]);

  const handleViewSubmission = (submission) => {
    setSelectedSubmission(submission);
    setShowViewModal(true);
    setShowActionsDropdown(null);
  };

  const handleArchiveClick = (id) => {
    setArchiveSubmissionId(id);
    setShowArchiveModal(true);
    setShowActionsDropdown(null);
  };

  const handleArchiveConfirm = async () => {
    setArchiveLoading(true);

    try {
      await axios.post('/editors/archiveSubmission', {
        submissionId: archiveSubmissionId
      });

      const endpoint = getSubmissionsEndpoint();
      const response = await axios.get(endpoint, {
        params: {
          page: currentPage,
          limit: itemsPerPage,
          search: debouncedSearchTerm
        }
      });

      if (response.data.success) {
        setRecentSubmissions(response.data.submissions);
        setTotalItems(response.data.total);
      }

      showToast('Submission archived successfully', 'success');
    } catch (error) {
      console.error('Error archiving submission:', error);
      showToast('Failed to archive submission', 'error');
    } finally {
      setArchiveLoading(false);
      setShowArchiveModal(false);
      setArchiveSubmissionId(null);
    }
  };

  const handleArchiveCancel = () => {
    setShowArchiveModal(false);
    setArchiveSubmissionId(null);
  };

  const handleTableAction = (action, submissionId) => {
    setShowActionsDropdown(null);

    switch (action) {
      case 'return':
        navigate(`/editors/process/return?a=${submissionId}`);
        break;
      case 'revise':
        navigate(`/editors/process/revise?a=${submissionId}`);
        break;
      case 'invite-reviewer':
        navigate(`/editors/process/invite?reviewer=true&a=${submissionId}`);
        break;
      case 'invite-editor':
        navigate(`/editors/process/invite?editor=true&a=${submissionId}`);
        break;
      case 'accept':
        navigate(`/editors/process/accept?a=${submissionId}`);
        break;
      case 'reject':
        navigate(`/editors/process/reject?a=${submissionId}`);
        break;
      default:
        showToast(`${action} action triggered`, 'info');
    }
  };

  const handlePageChange = (page) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const statsCards = [
    { label: 'Submissions',        value: stats.submissions,       icon: Upload,     color: 'bg-blue-500',   link: '/editors/dashboard' },
    { label: 'Authors',            value: stats.authors,           icon: Users,      color: 'bg-green-500',  link: '/editors/admin/authors' },
    { label: 'Reviewed',           value: stats.reviewed,          icon: CheckCircle, color: 'bg-purple-500', link: '/editors/submitted-reviews' },
    { label: 'Decisioned',         value: stats.decisioned,        icon: FileText,   color: 'bg-orange-500', link: '/editors/decisioned' },
    { label: 'Archived',           value: stats.archived,          icon: Archive,    color: 'bg-gray-500',   link: '/editors/admin/archived' },
    { label: 'Editor invitations', value: stats.editorInvitations, icon: UserPlus,   color: 'bg-red-500',    link: '/editors/editor-invitations' },
    { label: 'Pending Decisions',  value: stats.pendingDecisions,  icon: FileText,   color: 'bg-purple-600', link: '/editors/submitted-reviews' }
  ];

  const SectionalEditorStatCards = [
    { label: 'Submissions',        value: stats.submissions,       icon: Upload,     color: 'bg-blue-500',   link: '/editors/dashboard' },
    { label: 'Reviewed',           value: stats.reviewed,          icon: CheckCircle, color: 'bg-purple-500', link: '/editors/submitted-reviews' },
    { label: 'Decisioned',         value: stats.decisioned,        icon: FileText,   color: 'bg-orange-500', link: '/editors/decisioned' },
    { label: 'Editor invitations', value: stats.editorInvitations, icon: UserPlus,   color: 'bg-red-500',    link: '/editors/editor-invitations' },
    { label: 'Pending Decisions',  value: stats.pendingDecisions,  icon: FileText,   color: 'bg-purple-600', link: '/editors/submitted-reviews' }
  ];

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const renderChart = () => {
    if (chartLoading) {
      return (
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-solid border-purple-600 border-r-transparent"></div>
        </div>
      );
    }

    const commonProps = {
      data: chartData,
      margin: { top: 10, right: 30, left: 0, bottom: 0 }
    };

    switch (chartType) {
      case 'line':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} interval={Math.floor(chartData.length / 10)} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line type="monotone" dataKey="submissions" stroke="#7e22ce" strokeWidth={2}
                    dot={{ r: 4, fill: "#7e22ce" }} activeDot={{ r: 6, fill: "#4c1d95" }} name="Submissions" />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'area':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} interval={Math.floor(chartData.length / 10)} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Area type="monotone" dataKey="submissions" stroke="#7e22ce" fill="#c084fc" fillOpacity={0.3} name="Submissions" />
            </AreaChart>
          </ResponsiveContainer>
        );

      case 'bar':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} interval={Math.floor(chartData.length / 10)} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="submissions" fill="#7e22ce" radius={[4, 4, 0, 0]} name="Submissions" />
            </BarChart>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  if (loading && recentSubmissions.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-solid border-purple-600 border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-4 py-6">
      {/* Welcome Section */}
      <div className="admin col mb-6">
        <h3 className="text-2xl font-bold text-gray-800">
          Welcome, <span className="text-[--asfi-purple]">{user?.fullname || (user?.firstName + ' ' + user?.lastName) || 'Editor'}</span>
        </h3>
        <h5 className="text-gray-600 mt-1 capitalize">{user?.editorialLevel?.replace(/_/g, ' ') || 'Editor'}</h5>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8" id="stats">
        {(user?.editorialLevel === 'sectional_editor' ? SectionalEditorStatCards : statsCards).map((stat, index) => (
          <Link key={index} to={stat.link} className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow">
            <div className="flex flex-col items-center text-center">
              <div className={`${stat.color} p-3 rounded-full mb-3`}>
                <stat.icon className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">
                <span className="stat-value">{stat.value}</span> {stat.label}
              </h3>
            </div>
          </Link>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Submission Trends</h3>
            <div className="flex items-center gap-2">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-2 py-1 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="365">Last year</option>
              </select>
              <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                <button
                  onClick={() => setChartType('line')}
                  className={`px-2 py-1 text-sm ${chartType === 'line' ? 'bg-purple-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  Line
                </button>
                <button
                  onClick={() => setChartType('area')}
                  className={`px-2 py-1 text-sm border-l border-r border-gray-300 ${chartType === 'area' ? 'bg-purple-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  Area
                </button>
                <button
                  onClick={() => setChartType('bar')}
                  className={`px-2 py-1 text-sm ${chartType === 'bar' ? 'bg-purple-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  Bar
                </button>
              </div>
            </div>
          </div>
          <div className="h-64">{renderChart()}</div>
          <div className="mt-2 text-xs text-gray-500 text-center">
            Showing submission trends for the last {timeRange} days
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">Review Statistics</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>Completed Reviews</span>
                <span className="font-medium">
                  {stats.reviewed > 0 ? Math.round((stats.reviewed / stats.submissions) * 100) : 0}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full"
                  style={{ width: `${stats.reviewed > 0 ? (stats.reviewed / stats.submissions) * 100 : 0}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>Pending Reviews</span>
                <span className="font-medium">
                  {stats.submissions > 0 ? Math.round(((stats.submissions - stats.reviewed) / stats.submissions) * 100) : 0}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-yellow-500 h-2 rounded-full"
                  style={{ width: `${stats.submissions > 0 ? ((stats.submissions - stats.reviewed) / stats.submissions) * 100 : 0}%` }}
                ></div>
              </div>
            </div>
            <div className="pt-4 border-t border-gray-200">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-600">{stats.reviewed}</p>
                  <p className="text-xs text-gray-500">Completed</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-yellow-600">{stats.submissions - stats.reviewed}</p>
                  <p className="text-xs text-gray-500">Pending</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Submissions Table */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-3">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-gray-800">Recent Submissions</h3>
            <span className="text-xs text-gray-500">(most recent submission per article)</span>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search submissions..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full sm:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent text-sm"
              />
            </div>

            <button
              onClick={() => setShowFilters((s) => !s)}
              className="inline-flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 4h18l-7 8v6l-4 2v-8L3 4z" />
              </svg>
              Filters
              {activeFilterCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold rounded-full bg-purple-600 text-white">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Filter panel */}
        {showFilters && (
          <div className="mb-5 p-4 border border-gray-200 rounded-lg bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">From Date</label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => updateFilter("dateFrom", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">To Date</label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => updateFilter("dateTo", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => updateFilter("status", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s === "all" ? "All statuses" : formatStatus(s)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={filters.type}
                  onChange={(e) => updateFilter("type", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  {TYPE_OPTIONS.map((t) => (
                    <option key={t} value={t}>
                      {t === "all" ? "All types" : t}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                Special Collections
              </p>
              <div className="flex flex-wrap gap-4">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.isWomenInScience}
                    onChange={(e) => updateFilter("isWomenInScience", e.target.checked)}
                    className="w-4 h-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                  />
                  <span className="text-sm text-gray-700">Women in Contemporary Science in Africa</span>
                </label>

                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.isKidnappingForRansom}
                    onChange={(e) => updateFilter("isKidnappingForRansom", e.target.checked)}
                    className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-700">Kidnapping for Ransom</span>
                </label>

                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.isBelispointAcademic}
                    onChange={(e) => updateFilter("isBelispointAcademic", e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Belispoint Academic</span>
                </label>
              </div>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={resetFilters}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-white"
              >
                Reset
              </button>
              <button
                onClick={() => setShowFilters(false)}
                className="px-3 py-1.5 text-sm bg-[--asfi-purple] text-white rounded-lg hover:opacity-90"
              >
                Apply
              </button>
            </div>
          </div>
        )}

        {/* Active filter chips */}
        {activeFilterCount > 0 && (
          <div className="mb-4 flex flex-wrap gap-2">
            {filters.dateFrom && (
              <FilterChip label={`From: ${filters.dateFrom}`} onClear={() => updateFilter("dateFrom", "")} />
            )}
            {filters.dateTo && (
              <FilterChip label={`To: ${filters.dateTo}`} onClear={() => updateFilter("dateTo", "")} />
            )}
            {filters.status !== "all" && (
              <FilterChip label={`Status: ${formatStatus(filters.status)}`} onClear={() => updateFilter("status", "all")} />
            )}
            {filters.type !== "all" && (
              <FilterChip label={`Type: ${filters.type}`} onClear={() => updateFilter("type", "all")} />
            )}
            {filters.isWomenInScience && (
              <FilterChip label="Women in Contemporary Science" onClear={() => updateFilter("isWomenInScience", false)} />
            )}
            {filters.isKidnappingForRansom && (
              <FilterChip label="Kidnapping for Ransom" onClear={() => updateFilter("isKidnappingForRansom", false)} />
            )}
            {filters.isBelispointAcademic && (
              <FilterChip label="Belispoint Academic" onClear={() => updateFilter("isBelispointAcademic", false)} />
            )}
            <button
              onClick={resetFilters}
              className="text-xs text-gray-500 hover:text-gray-700 underline"
            >
              Clear all
            </button>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submission</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reviewer Invitations</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Editor Invitations</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentSubmissions.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    No submissions found
                  </td>
                </tr>
              ) : (
                recentSubmissions.map((submission) => (
                  <tr key={submission.revision_id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="max-w-md">
                        <p
                          className="text-sm font-medium text-gray-900 cursor-pointer hover:text-[--asfi-blue] text-purple-900 truncate"
                          onClick={() => handleViewSubmission(submission)}
                        >
                          {submission.title}
                        </p>
                        <p className="text-xs text-[--asfi-purple] mt-1">{submission.revision_id}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          By: {submission.authors}
                        </p>
                        {submission.newReviews > 0 && (
                          <button
                            onClick={() => navigate(`/editors/decision/${submission.revision_id}`)}
                            className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                            title="New submitted reviews awaiting your decision"
                          >
                            <span className="relative flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                            </span>
                            {submission.newReviews} new review{submission.newReviews > 1 ? 's' : ''}
                          </button>
                        )}
                        {submission.isWomenInScience && (
                          <span className="inline-flex items-center px-2 py-0.5 mt-2 rounded text-xs font-medium bg-pink-100 text-pink-800">
                            Women in Contemporary Science in Africa
                          </span>
                        )}
                        {submission.isBelispointAcademic && (
                          <span className="inline-flex items-center px-2 py-0.5 mt-2 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            Belispoint Academic
                          </span>
                        )}
                        {submission.isKidnappingForRansom && (
                          <span className="inline-flex items-center px-2 py-0.5 mt-2 rounded text-xs font-medium bg-red-100 text-red-800">
                            Kidnapping For Ransom
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm text-gray-900">{submission.date}</p>
                      <p className="text-xs text-gray-500">{submission.type}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`status-text ${getStatusClass(submission.status)}`}>
                        {formatStatus(submission.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <ul className="text-sm space-y-1">
                        <li className="text-green-600">Accepted: {submission.reviewerInvitations?.accepted || 0}</li>
                        <li className="text-red-600">Declined: {submission.reviewerInvitations?.declined || 0}</li>
                        <li className="text-yellow-600">Pending: {submission.reviewerInvitations?.pending || 0}</li>
                      </ul>
                    </td>
                    <td className="px-6 py-4">
                      <ul className="text-sm space-y-1">
                        <li className="text-green-600">Accepted: {submission.editorInvitations?.accepted || 0}</li>
                        <li className="text-red-600">Declined: {submission.editorInvitations?.declined || 0}</li>
                        <li className="text-yellow-600">Pending: {submission.editorInvitations?.pending || 0}</li>
                      </ul>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewSubmission(submission)}
                          className="text-sm font-medium text-[--asfi-purple] hover:text-[--asfi-blue]"
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleArchiveClick(submission.revision_id)}
                          className="text-sm font-medium text-gray-500 hover:text-gray-700"
                        >
                          Archive
                        </button>
                        <div className="relative">
                          <button
                            onClick={() => setShowActionsDropdown(showActionsDropdown === submission.revision_id ? null : submission.revision_id)}
                            className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>

                          {showActionsDropdown === submission.revision_id && (
                            <>
                              <div className="fixed inset-0 z-30" onClick={() => setShowActionsDropdown(null)} />
                              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-40 border border-gray-200">
                                <button
                                  onClick={() => { handleViewSubmission(submission); setShowActionsDropdown(null); }}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  View
                                </button>
                                <button
                                  onClick={() => handleTableAction('return', submission.revision_id)}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  Return For Correction
                                </button>
                                <button
                                  onClick={() => handleTableAction('revise', submission.revision_id)}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  Revise
                                </button>
                                <button
                                  onClick={() => handleTableAction('invite-reviewer', submission.revision_id)}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  Invite Reviewer
                                </button>
                                <button
                                  onClick={() => handleTableAction('invite-editor', submission.revision_id)}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  Invite Editor
                                </button>
                                <button
                                  onClick={() => handleTableAction('accept', submission.revision_id)}
                                  className="block w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50"
                                >
                                  Accept
                                </button>
                                <button
                                  onClick={() => handleTableAction('reject', submission.revision_id)}
                                  className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                >
                                  Reject
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Page {currentPage} of {totalPages} ({totalItems} total items)
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              <span className="px-3 py-1 text-sm">Page {currentPage}</span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-1"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="footer text-center py-4 border-t border-gray-200">
        <p className="text-sm text-gray-500">
          {new Date().getFullYear()} © ASFI - all rights reserved
        </p>
      </footer>

      {/* View Submission Modal */}
      {showViewModal && selectedSubmission && (
        <SubmissionDetailsModal
          submission={selectedSubmission}
          onClose={() => {
            setShowViewModal(false);
            setSelectedSubmission(null);
          }}
          onArchiveClick={handleArchiveClick}
          onCombineFiles={handleCombineFiles}
          combiningFiles={combiningFiles}
          combineProgress={combineProgress}
          combineResult={combineResult}
          onSendReminder={handleOpenReminderModal}
        />
      )}

      {/* Send Reminder Modal */}
      {showReminderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[70]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <Mail className="w-6 h-6 text-orange-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">Send Review Reminder</h3>
                  <p className="text-sm text-gray-500 mt-1">Send a reminder to the reviewer</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-700">Reviewer:</p>
                  <p className="text-sm text-gray-900 mt-1">{reminderData.reviewerEmail}</p>
                </div>

                {reminderData.daysOverdue > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-800 flex items-center">
                      <AlertTriangle className="w-4 h-4 mr-2" />
                      This review is {reminderData.daysOverdue} days overdue
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Custom Message (Optional)
                  </label>
                  <textarea
                    rows="4"
                    value={customReminderMessage}
                    onChange={(e) => setCustomReminderMessage(e.target.value)}
                    placeholder="Add any additional notes or instructions..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-xs text-yellow-800">
                    <strong>Note:</strong> The reminder will be sent immediately. The reviewer will receive an email with a link to their review.
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowReminderModal(false);
                    setCustomReminderMessage('');
                    setReminderData({
                      reviewId: null,
                      articleId: null,
                      reviewerEmail: null,
                      dueDate: null,
                      daysOverdue: 0
                    });
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendReminder}
                  disabled={reminderLoading}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {reminderLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      Send Reminder
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Archive Confirmation Modal */}
      {showArchiveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">Archive Submission</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Are you sure you want to archive this submission?
                  </p>
                </div>
              </div>

              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> Archived submissions can be restored later from the Archived Papers section. Authors will be notified about this action.
                </p>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={handleArchiveCancel}
                  disabled={archiveLoading}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleArchiveConfirm}
                  disabled={archiveLoading}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {archiveLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Archiving...
                    </>
                  ) : (
                    <>
                      <Archive className="w-4 h-4" />
                      Confirm Archive
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .status-text {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 500;
        }
        .status-blue   { background-color: #dbeafe; color: #1e40af; }
        .status-yellow { background-color: #fef3c7; color: #92400e; }
        .status-orange { background-color: #ffedd5; color: #9a3412; }
        .status-green  { background-color: #d1fae5; color: #065f46; }
        .status-red    { background-color: #fee2e2; color: #991b1b; }
        .status-purple { background-color: #f3e8ff; color: #6b21a8; }
        .status-gray   { background-color: #f3f4f6; color: #1f2937; }
      `}</style>
    </div>
  );
};

export default AdminDashboard;